<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <LeadTreeCombobox
      :label="props.label"
      :required="props.required"
      :placeholder="props.placeholder"
      :items="availableCompanies"
      getOptionLabel="name"
      getOptionValue="id"
      :modelValue="comboboxModelValue"
      @update:modelValue="handleSelectionChange"
      @search="onSearch"
      :error="errors"
      :disabled="props.disabled"
      displayInitials
      v-bind="filteredAttrs"
      :free-text-entry="props.freeTextEntry"
      search-mode="remote"
      :clearable="props.clearable"
      :loading="isSearching">
      <template #hint v-if="props.hint">{{ props.hint }}</template>
    </LeadTreeCombobox>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, useAttrs } from 'vue';
import { debounce } from '@prospective/lithium';
import LeadTreeCombobox from '@/components/form/LeadTreeCombobox.vue';

import { fetchCompanies, fetchCompany } from '@/api/company';
import type { ICompany } from '@/types/company';
import translator from '@/locale/translator';

const companyId = defineModel<number | undefined>('companyId', {
  default: undefined,
});
const companyName = defineModel<string | undefined>('companyName', {
  default: undefined,
});
const companyLogoImageId = defineModel<number | undefined>('companyLogoImageId', {
  default: undefined,
});

const props = withDefaults(
  defineProps<{
    label?: string;
    placeholder?: string;
    hint?: string;
    errors?: string[];
    required?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    freeTextEntry?: boolean;
  }>(),
  {
    placeholder: translator.get('common.choose_company'),
    errors: () => [],
    required: true,
    clearable: false,
    disabled: false,
    freeTextEntry: false,
  }
);

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: className, style, ...rest } = attrs;
  return rest;
});

const availableCompanies = ref<(ICompany & { imageUrl?: string })[]>([]);
const isManualFreeText = ref(false);
const isSearching = ref(false);

function findCompanyInTree(
  nodes: (ICompany & { imageUrl?: string })[],
  predicate: (c: ICompany) => boolean
): (ICompany & { imageUrl?: string }) | null {
  for (const n of nodes) {
    if (predicate(n)) return n;
    if (Array.isArray(n.children) && n.children.length > 0) {
      const found = findCompanyInTree(n.children as (ICompany & { imageUrl?: string })[], predicate);
      if (found) return found;
    }
  }
  return null;
}

const selectedOption = computed(() => {
  if (!companyId.value && !companyName.value) {
    return null;
  }

  // When free text was manually entered, don't return a synthetic object
  if (isManualFreeText.value && !companyId.value) {
    return null;
  }

  const found = findCompanyInTree(
    availableCompanies.value,
    (c) => c.id === companyId.value || c.name === companyName.value
  );
  if (found) {
    return found;
  }

  return {
    id: companyId.value,
    name: companyName.value,
  } as ICompany;
});

// When free text is active, pass the raw string so the combobox's
// freeTextEntry template branch (`typeof modelValue === 'string'`) works
const comboboxModelValue = computed(() => {
  if (isManualFreeText.value && !companyId.value && companyName.value) {
    return companyName.value;
  }
  return selectedOption.value;
});

function mapCompany(company: ICompany): ICompany & { imageUrl?: string } {
  const mapped: ICompany & { imageUrl?: string } = {
    ...company,
    imageUrl: company?.logo_image?.data?.proxy?.url_thumbnail ?? undefined,
  };
  if (Array.isArray(company.children) && company.children.length > 0) {
    mapped.children = company.children.map(mapCompany);
  }
  return mapped;
}

const searchCompanyDebounced = debounce((query: string) => {
  fetchCompanies({ search: query, resolve: 'logo_image', tree_type: 'tree' })
    .then((response) => {
      availableCompanies.value = (response.data as unknown as ICompany[]).map(mapCompany);
    })
    .finally(() => {
      isSearching.value = false;
    });
}, 300);

// Flip the spinner on immediately when the user types so the inline loader
// replaces the clear icon during the debounce window too. Cleared in the
// fetch's .finally above.
function onSearch(query: string) {
  isSearching.value = true;
  searchCompanyDebounced(query);
}

function handleSelectionChange(company: ICompany | string | null) {
  if (!company) {
    companyId.value = undefined;
    companyName.value = undefined;
    companyLogoImageId.value = undefined;
    isManualFreeText.value = false;
    return;
  }

  // When freeTextEntry creates an entry, the value is a plain string
  if (typeof company === 'string') {
    isManualFreeText.value = true;
    // Look up the company in availableCompanies
    const found = findCompanyInTree(availableCompanies.value, (c) => c.name === company);
    companyLogoImageId.value = found?.logo_image_id ?? undefined;
    companyId.value = found?.id ?? undefined;
    companyName.value = company;
    return;
  }

  isManualFreeText.value = false;
  companyName.value = company.name;
  companyId.value = company.id;
  companyLogoImageId.value = company.logo_image_id;
}

// Handles initial load when only name is set
watch(
  companyName,
  async (name) => {
    if (name && !companyId.value && !isManualFreeText.value) {
      const res = await fetchCompanies({ search: name, resolve: 'logo_image', tree_type: 'tree' });
      const data = (res.data as unknown as ICompany[]).map(mapCompany);
      availableCompanies.value = data;
      if (Array.isArray(data) && data.length) {
        const found = findCompanyInTree(data, (c) => c.name === name);
        if (found) {
          companyId.value = found.id;
          companyLogoImageId.value = found.logo_image_id;
        }
      }
    }
  },
  { immediate: true }
);

// Handles initial load when ID is set
watch(
  companyId,
  async (id) => {
    if (id) {
      // If we already have this company in the list, no need to fetch
      const existing = findCompanyInTree(availableCompanies.value, (c) => c.id === id);
      if (existing) {
        companyLogoImageId.value = existing.logo_image_id;
        return;
      }

      await fetchCompany(id, { resolve: 'logo_image' }).then((res) => {
        if (res.data.name) {
          if (!companyName.value) {
            companyName.value = res.data.name;
          }
          companyLogoImageId.value = res.data.logo_image_id;
          availableCompanies.value = [mapCompany(res.data as unknown as ICompany)];
        } else {
          companyId.value = undefined;
          companyLogoImageId.value = undefined;
        }
      });
    }
  },
  { immediate: true }
);
</script>
